import assertNever from "assert-never";
import { LocalDate } from "@js-joda/core";
import * as messages from "../../messages";
import {
  AgencyId,
  AgencyMemberId,
  TrainingCenterBundleDueDateId,
  TrainingCenterBundleItemFullDescriptionId,
  TrainingCenterBundleItemId,
  TrainingCenterBundleItemTestQuestionId,
  TrainingCenterBundleItemVideoEngmtQuestionId,
  TrainingCenterBundleItemVideoMediaOverlayId,
} from "../../messages/ObjectIds";
import {
  TrainingCenterBundleCertificate,
  TrainingCenterBundleItemDisplayData,
  TrainingCenterBundleItemVideoEngagementQuestionDisplayData,
  TrainingCenterBundleItemVideoMediaOverlayData,
  TrainingCenterBundleItemVideoMediaOverlayPosition,
  TrainingCenterCaregiverPDFWatchData,
  TrainingCenterCaregiverVideoWatchData,
  TrainingCenterDisplayData,
  TrainingCenterPDFInfo,
  TrainingCenterTestParams,
  TrainingCenterTestQuestionDisplayData,
  TrainingCenterTranslated,
  TrainingCenterVideoInfo,
  TrainingCenterVideoSubtitles,
} from "../../messages/TrainingCenter";
import { KeyedList, List } from "../../utils/List";

export interface CaregiverTrainingCenterBundles {
  bundles: KeyedList<TrainingCenterCaregiverBundle, "bundleDueDateId">;
}

export interface TrainingCenterCaregiverBundle {
  agencyId: AgencyId;

  // Commented out because the danger is too great for confusion. (We can
  // comment it out because we don't actually need it anywhere)
  //
  // bundleId: TrainingCenterBundleId;

  bundleDueDateId: TrainingCenterBundleDueDateId;
  bundleDueDateYear: number;
  visibleDate: LocalDate;
  completionDate: LocalDate;
  complianceDate: LocalDate;

  bundleParams: TrainingCenterBundleParams;
  bundleDisplayData: KeyedList<TrainingCenterTranslated<TrainingCenterDisplayData>, "language">;

  completionCertificate: TrainingCenterBundleCertificate | null;

  bundleItems: KeyedList<TrainingCenterBundleItem, "bundleItemId">;
  showDuration: boolean;
}

export interface TrainingCenterBundleParams {
  chatAgencyMemberId: AgencyMemberId;
  signingAgencyMemberId: AgencyMemberId;
  signingAgencyMemberTitle: string;
  enforceWatchOrder: boolean;
  requiredForBillingCompliance: boolean;
  canWatchDuringVisitShift: boolean;
  showBundleAfterDueDate: boolean;
  caregiverCertifications: string[];
  isOrientation: boolean;
}

export interface TrainingCenterBundleItem {
  bundleItemId: TrainingCenterBundleItemId;

  timeForCertificateMinutes: number | null;

  media: TrainingCenterBundleItemMedia;

  itemCompleted: boolean;
  itemExempt: boolean;

  caregiverSignatureDataURL: string | null;

  /**
   * The test becomes unlocked when the caregiver has watched enough of the
   * training material (e.g. Video, PDF...)
   */
  testUnlocked: boolean;

  /**
   * How many attempts has this caregiver submitted the test. If this is
   * equal to or greater than test max attempts from `testParams` then the
   * test questions should be grayed out.
   */
  testNumAttempts: number;

  testParams: TrainingCenterTestParams;

  displayData: KeyedList<TrainingCenterTranslated<TrainingCenterBundleItemDisplayData>, "language">;
  currentTestQuestions: KeyedList<TrainingCenterCaregiverTestQuestion, "questionId">;
  fullDescriptions: KeyedList<TrainingCenterBundleItemFullDescription, "itemId">;
}

export interface TrainingCenterBundleItemVideoEngagementQuestion {
  questionId: TrainingCenterBundleItemVideoEngmtQuestionId;
  atSecond: number;
  duration: number;

  displayData: KeyedList<
    TrainingCenterTranslated<TrainingCenterBundleItemVideoEngagementQuestionDisplayData>,
    "language"
  >;
}

export interface TrainingCenterBundleItemVideoMediaOverlay {
  itemId: TrainingCenterBundleItemVideoMediaOverlayId;
  atSecond: number;
  duration: number;
  position: TrainingCenterBundleItemVideoMediaOverlayPosition;

  overlayData: KeyedList<
    TrainingCenterTranslated<TrainingCenterBundleItemVideoMediaOverlayData>,
    "language"
  >;
}

export interface EngagementChosenAnswer {
  value: string;
  atSecond: number;
}

export interface TrainingCenterBundleCourseDisplayData {
  sectionHeader: string | null;
  sectionDescription: string | null;
}
export interface TrainingCenterBundleCourse {
  displayData: KeyedList<
    TrainingCenterTranslated<TrainingCenterBundleCourseDisplayData>,
    "language"
  >;
  bundleItems: TrainingCenterBundleItem[];
}

export interface TrainingCenterCaregiverTestQuestion {
  questionId: TrainingCenterBundleItemTestQuestionId;
  displayData: KeyedList<
    TrainingCenterTranslated<TrainingCenterTestQuestionDisplayData>,
    "language"
  >;

  /**
   * The currently selected answer, or the submitted answer from the last
   * submitted test
   */
  markedAnswer: number | null;

  /**
   * The marked answer is known to be the correct answer (its value is 0 and
   * was submitted as a test)
   */
  markedAnswerCorrect: boolean;

  /**
   * Submitted answers that were incorrect (from all previous test
   * submissions)
   */
  incorrectlyGuessedAnswers: number[];
}

export interface TrainingCenterBundleItemFullDescription {
  itemId: TrainingCenterBundleItemFullDescriptionId;
  fullDescriptionData: KeyedList<TrainingCenterTranslated<FullDescriptionData>, "language">;
  isPlaying: isPlayingDescriptionType;
}

export interface FullDescriptionData {
  title?: string;
  subtitle?: string;
  text?: string;
  imageUrl?: string;
  imageDescription?: string;
  imageFileKey?: string;
  speechFiles: {
    titleSpeechAudioFileUrl?: string;
    titleSpeechMarksFileUrl?: string;
    subtitleSpeechAudioFileUrl?: string;
    subtitleSpeechMarksFileUrl?: string;
    textSpeechAudioFileUrl?: string;
    textSpeechMarksFileUrl?: string;
  };
}

export type isPlayingDescriptionType = "title" | "subtitle" | "text" | false;

export interface FullDescriptionSpeechMark {
  index: number;
  time: number;
  type: "word";
  start: number;
  end: number;
  value: string;
  to: number;
  found: boolean;
}

export type TrainingCenterBundleItemMedia =
  | TrainingCenterBundleItemMedia.Video
  | TrainingCenterBundleItemMedia.PDF
  | TrainingCenterBundleItemMedia.TestOnly;

export type TrainingCenterBundleVideoSource = "YOUTUBE" | "VIDEO_URL";

export declare namespace TrainingCenterBundleItemMedia {
  export interface Video {
    type: "Video";
    requiredWatchTimePercent: number;

    source: TrainingCenterBundleVideoSource;
    engagementQuestions: List<TrainingCenterBundleItemVideoEngagementQuestion>;
    mediaOverlays: List<TrainingCenterBundleItemVideoMediaOverlay>;
    videoInfo: KeyedList<TrainingCenterTranslated<TrainingCenterVideoInfo>, "language">;
    videoSubtitles: KeyedList<TrainingCenterTranslated<TrainingCenterVideoSubtitles>, "language">;
    videoWatchData: KeyedList<
      TrainingCenterTranslated<TrainingCenterCaregiverVideoWatchData>,
      "language"
    >;
  }

  export interface PDF {
    type: "PDF";
    requiredViewTimeMinutes: number;
    pdfFileDownloadable: boolean;
    pdfInfo: KeyedList<TrainingCenterTranslated<TrainingCenterPDFInfo>, "language">;
    pdfWatchData: TrainingCenterCaregiverPDFWatchData;
  }

  export interface TestOnly {
    type: "TestOnly";
  }
}

export function fromCaregiverTrainingCenterBundles(
  msg: messages.CaregiverTrainingCenterBundles
): CaregiverTrainingCenterBundles {
  return {
    bundles: KeyedList.from(msg.bundles.map(fromTrainingCenterCaregiverBundle))("bundleDueDateId"),
  };
}

export function fromTrainingCenterCaregiverBundle(
  msg: messages.TrainingCenterCaregiverBundle
): TrainingCenterCaregiverBundle {
  return {
    agencyId: msg.agencyId,
    // bundleId: msg.bundleId,
    bundleDueDateId: msg.bundleDueDateId,
    bundleDueDateYear: msg.bundleDueDateYear,
    visibleDate: msg.visibleDate,
    completionDate: msg.completionDate,
    complianceDate: msg.complianceDate,
    bundleParams: fromTrainingCenterBundleParams(msg.bundleParams),
    bundleDisplayData: KeyedList.from(msg.bundleDisplayData)("language"),
    completionCertificate: msg.completionCertificate,
    bundleItems: KeyedList.from(msg.bundleItems.map(fromTrainingCenterBundleItem))("bundleItemId"),
    showDuration: msg.showDuration,
  };
}

export function fromTrainingCenterBundleParams(
  msg: messages.TrainingCenterBundleParams
): TrainingCenterBundleParams {
  return {
    chatAgencyMemberId: msg.chatAgencyMemberId,
    signingAgencyMemberId: msg.signingAgencyMemberId,
    signingAgencyMemberTitle: msg.signingAgencyMemberTitle,
    enforceWatchOrder: msg.enforceWatchOrder,
    requiredForBillingCompliance: msg.requiredForBillingCompliance,
    canWatchDuringVisitShift: msg.canWatchDuringVisitShift,
    showBundleAfterDueDate: msg.showBundleAfterDueDate,
    caregiverCertifications: msg.caregiverCertifications,
    isOrientation: msg.isOrientation,
  };
}

export function fromTrainingCenterBundleItem(
  msg: messages.TrainingCenterBundleItem
): TrainingCenterBundleItem {
  return {
    bundleItemId: msg.bundleItemId,
    timeForCertificateMinutes: msg.timeForCertificateMinutes,
    media: fromTrainingCenterBundleItemMedia(msg.media),
    itemCompleted: msg.itemCompleted,
    itemExempt: msg.itemExempt,
    testUnlocked: msg.testUnlocked,
    caregiverSignatureDataURL: msg.caregiverSignatureDataURL,
    testNumAttempts: msg.testNumAttempts,
    testParams: msg.testParams,
    displayData: KeyedList.from(msg.displayData)("language"),
    currentTestQuestions: KeyedList.from(
      msg.currentTestQuestions.map(fromTrainingCenterCaregiverTestQuestion)
    )("questionId"),
    fullDescriptions: KeyedList.from(
      msg.fullDescriptions.map((fullDescriptionRow) => ({
        ...fullDescriptionRow,
        fullDescriptionData: KeyedList.from(fullDescriptionRow.fullDescriptionData)("language"),
        isPlaying: <isPlayingDescriptionType>false,
      }))
    )("itemId"),
  };
}

export function fromTrainingCenterBundleItemMedia(
  msg: messages.TrainingCenterBundleItemMedia
): TrainingCenterBundleItemMedia {
  switch (msg.type) {
    case "Video":
      return {
        type: "Video",
        requiredWatchTimePercent: msg.requiredWatchTimePercent,
        source: msg.source,
        engagementQuestions: List.from(
          msg.engagementQuestions.map((question) => ({
            ...question,
            displayData: KeyedList.from(question.displayData)("language"),
          }))
        ),
        mediaOverlays: List.from(
          msg.mediaOverlays.map((overlay) => ({
            ...overlay,
            overlayData: KeyedList.from(overlay.overlayData)("language"),
          }))
        ),
        videoInfo: KeyedList.from(msg.videoInfo)("language"),
        videoSubtitles: KeyedList.from(msg.videoSubtitles)("language"),
        videoWatchData: KeyedList.from(msg.videoWatchData)("language"),
      };
    case "PDF":
      return {
        type: "PDF",
        requiredViewTimeMinutes: msg.requiredViewTimeMinutes,
        pdfFileDownloadable: msg.pdfFileDownloadable,
        pdfInfo: KeyedList.from(msg.pdfInfo)("language"),
        pdfWatchData: msg.pdfWatchData,
      };
    case "TestOnly":
      return {
        type: "TestOnly",
      };
    default:
      return assertNever(msg);
  }
}

export function fromTrainingCenterCaregiverTestQuestion(
  msg: messages.TrainingCenterCaregiverTestQuestion
): TrainingCenterCaregiverTestQuestion {
  return {
    questionId: msg.questionId,
    displayData: KeyedList.from(msg.displayData)("language"),
    markedAnswer: msg.markedAnswer,
    markedAnswerCorrect: msg.markedAnswerCorrect,
    incorrectlyGuessedAnswers: msg.incorrectlyGuessedAnswers,
  };
}
